import { FC, KeyboardEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
    calculatePricingDetails,
    CourseData,
    FORMAT_LABELS,
    formatPaymentPeriod,
    PricingOption,
    TYPE_LABELS,
    useWebsiteCatalog,
} from 'entities/course-catalog'
import { admissionModel } from '..'
import { LegalFormDisclaimer } from 'shared/ui/legal-form-disclaimer'
import { getNameError, getPhoneError, validateLeadFields } from 'shared/lib/lead-form-validation'
import { useLeadSubmission } from 'shared/hooks/use-lead-submission'
import { popoutModel } from 'shared/ui/popout-root'

import styles from './styles.module.scss'

const isSamePricing = (left: PricingOption | null | undefined, right: PricingOption | null | undefined) =>
    left?.format === right?.format && left?.type === right?.type && left?.price === right?.price

type SubmittedSnapshot = {
    course: CourseData
    pricing: PricingOption
}

const onRadioKeyDown = <T,>(
    event: KeyboardEvent<HTMLButtonElement>,
    options: readonly T[],
    current: T,
    select: (option: T) => void,
    idFor: (option: T) => string,
) => {
    const currentIndex = options.indexOf(current)
    if (currentIndex === -1 || !options.length) return

    let nextIndex: number | null = null
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = options.length - 1
    if (nextIndex === null) return

    event.preventDefault()
    const next = options[nextIndex]
    select(next)
    window.requestAnimationFrame(() => document.getElementById(idFor(next))?.focus())
}

export const Admission: FC = () => {
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [touched, setTouched] = useState({ name: false, phone: false })
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [consentAccepted, setConsentAccepted] = useState(false)
    const [priceWasUpdated, setPriceWasUpdated] = useState(false)
    const [sent, setSent] = useState(false)
    const [submittedSnapshot, setSubmittedSnapshot] = useState<SubmittedSnapshot | null>(null)
    const successCloseRef = useRef<HTMLButtonElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [successHeight, setSuccessHeight] = useState<number>()

    const course = admissionModel.selectors.useCourseSelect()
    const pricingSelect = admissionModel.selectors.usePricingSelect()
    const { catalog, status: catalogStatus, error: catalogError, refreshing, retry: retryCatalog } = useWebsiteCatalog()
    const { pending, error: submitError, submit } = useLeadSubmission()

    const selectedCourseId = course?.courseData?.id as string | undefined
    const catalogCourse = useMemo(
        () => catalog?.courses.find((item) => item.id === selectedCourseId) ?? null,
        [catalog, selectedCourseId],
    )
    const selectedPricing = (course?.pricingOption ?? pricingSelect) as PricingOption | null
    const displayedCourse = submittedSnapshot?.course ?? catalogCourse
    const displayedPricing = submittedSnapshot?.pricing ?? selectedPricing

    useLayoutEffect(() => {
        if (!sent) return
        const button = successCloseRef.current
        const dialog = button?.closest<HTMLElement>('[role="dialog"]')
        if (dialog) dialog.scrollTop = 0
        button?.focus({ preventScroll: true })
    }, [sent])

    useEffect(() => {
        if (!catalogCourse) return

        const requestedPricing = selectedPricing
        const matchingPricing = requestedPricing
            ? catalogCourse.pricing.find((item) => item.format === requestedPricing.format && item.type === requestedPricing.type)
            : null
        const nextPricing = matchingPricing ?? catalogCourse.pricing[0] ?? null

        if (!nextPricing) return
        if (requestedPricing && requestedPricing.price !== nextPricing.price) setPriceWasUpdated(true)

        if (
            course?.courseData !== catalogCourse ||
            !isSamePricing(course?.pricingOption, nextPricing) ||
            !isSamePricing(pricingSelect, nextPricing)
        ) {
            admissionModel.events.setCourseSelect({
                name: catalogCourse.title,
                courseData: catalogCourse,
                pricingOption: nextPricing,
            })
            admissionModel.events.setPricingSelect(nextPricing)
        }
    }, [catalogCourse, course?.courseData, course?.pricingOption, pricingSelect, selectedPricing])

    const fieldErrors = validateLeadFields(name, phone)
    const nameError = getNameError(name, touched.name || submitAttempted)
    const phoneError = getPhoneError(phone, touched.phone || submitAttempted)
    const consentError = submitAttempted && !consentAccepted ? 'Подтвердите согласие.' : null
    const pricingDetails = displayedCourse && displayedPricing
        ? calculatePricingDetails(displayedCourse, displayedPricing)
        : null
    const priceConfirmed = Boolean(submittedSnapshot) || (catalogStatus === 'ready' && !refreshing)

    const availableFormats = useMemo(
        () => Array.from(new Set(displayedCourse?.pricing.map((item) => item.format) ?? [])),
        [displayedCourse],
    )
    const availableTypes = useMemo(
        () => displayedCourse?.pricing.filter((item) => item.format === displayedPricing?.format) ?? [],
        [displayedCourse, displayedPricing?.format],
    )

    const selectPricing = (pricing: PricingOption) => {
        if (!catalogCourse || refreshing || pending) return
        setPriceWasUpdated(false)
        admissionModel.events.setCourseSelect({
            name: catalogCourse.title,
            courseData: catalogCourse,
            pricingOption: pricing,
        })
        admissionModel.events.setPricingSelect(pricing)
    }

    const onSubmit = async () => {
        setSubmitAttempted(true)
        if (fieldErrors.name || fieldErrors.phone || !consentAccepted || !catalogCourse || !selectedPricing || catalogStatus !== 'ready' || refreshing || pending) return

        const snapshot = { course: catalogCourse, pricing: selectedPricing }
        const container = containerRef.current
        const submittedHeight = container ? Math.min(container.getBoundingClientRect().height, container.closest<HTMLElement>('[role="dialog"]')?.clientHeight ?? Infinity) : undefined
        setSubmittedSnapshot(snapshot)

        const submitted = await submit({
            name: name.trim(),
            phone: phone.trim(),
            course: snapshot.course.title,
            courseData: snapshot.course,
            pricingOption: snapshot.pricing,
            train: admissionModel.getTrainingId(snapshot.pricing),
            pricing: snapshot.pricing,
        })
        if (submitted) {
            setSuccessHeight(submittedHeight)
            setSent(true)
        }
        else setSubmittedSnapshot(null)
    }

    if (!selectedCourseId) {
        return <div className={styles.admission}><p className={styles.missingCourse} role="alert">Не удалось определить курс. Вернитесь к каталогу и выберите направление.</p></div>
    }

    return (
        <section className={styles.admission} aria-labelledby="admission-title">
            <div ref={containerRef} className={`${styles.container} ${sent ? styles.successContainer : ''}`} style={sent ? { minHeight: successHeight } : undefined}>
                {!sent && <header className={styles.header}>
                    <h2 id="admission-title" className={styles.title}>Запись на курс</h2>
                    {displayedCourse ? (
                        <p className={styles.subtitle}>
                            <strong className={styles.courseName}>{displayedCourse.title}</strong><br />
                            {formatPaymentPeriod(displayedCourse.durationMonths)} · {displayedCourse.frequency}
                        </p>
                    ) : <p className={styles.subtitle}>Проверяем актуальные условия обучения.</p>}
                </header>}

                {displayedCourse && displayedPricing && !sent ? (
                    <div className={styles.selection}>
                        <fieldset className={styles.optionGroup}>
                            <legend>Формат занятий</legend>
                            <div className={styles.optionList} role="radiogroup" aria-label="Формат занятий">
                                {availableFormats.map((format) => {
                                    const active = displayedPricing.format === format
                                    const next = displayedCourse.pricing.find((item) => item.format === format && item.type === displayedPricing.type)
                                        ?? displayedCourse.pricing.find((item) => item.format === format)
                                    return (
                                        <button
                                            key={format}
                                            id={`admission-format-${format}`}
                                            type="button"
                                            className={active ? styles.optionActive : styles.option}
                                            role="radio"
                                            aria-checked={active}
                                            aria-label={FORMAT_LABELS[format]}
                                            tabIndex={active ? 0 : -1}
                                            disabled={refreshing || pending}
                                            onKeyDown={(event) => onRadioKeyDown(event, availableFormats, format, (nextFormat) => {
                                                const next = displayedCourse.pricing.find((item) => item.format === nextFormat && item.type === displayedPricing.type)
                                                    ?? displayedCourse.pricing.find((item) => item.format === nextFormat)
                                                if (next) selectPricing(next)
                                            }, (nextFormat) => `admission-format-${nextFormat}`)}
                                            onClick={() => next && selectPricing(next)}
                                        >
                                            {FORMAT_LABELS[format]}
                                        </button>
                                    )
                                })}
                            </div>
                        </fieldset>

                        <div className={styles.typeField}>
                            <label htmlFor="admission-type">Тип занятий</label>
                            <select id="admission-type" value={displayedPricing.type} aria-describedby={displayedPricing.type === 'closed_group' ? 'admission-type-hint' : undefined} disabled={refreshing || pending} onChange={event => {
                                const next = availableTypes.find(pricing => pricing.type === event.currentTarget.value)
                                if (next) selectPricing(next)
                            }}>
                                {availableTypes.map(pricing => <option key={pricing.type} value={pricing.type}>{TYPE_LABELS[pricing.type]}</option>)}
                            </select>
                        </div>
                        <p id="admission-type-hint" className={`${styles.typeHint} ${displayedPricing.type !== 'closed_group' ? styles.typeHintHidden : ''}`} aria-hidden={displayedPricing.type !== 'closed_group'}>Размер закрытой группы уточним при связи.</p>
                    </div>
                ) : null}

                {!sent ? (
                    <div className={styles.priceShell} aria-live="polite" aria-busy={!priceConfirmed && catalogStatus !== 'error'}>
                        <div className={styles.priceCard} aria-hidden={!priceConfirmed} style={!priceConfirmed ? { visibility: 'hidden' } : undefined}>
                            {pricingDetails && displayedPricing ? <>
                                <span>{pricingDetails.billingUnit === 'course' ? 'Стоимость курса' : 'Оплата в месяц'}</span>
                                <strong>{displayedPricing.price.toLocaleString('ru-RU')} ₽</strong>
                                <p>
                                    {pricingDetails.billingUnit === 'course'
                                        ? 'Сумма рассчитана за весь курс.'
                                        : pricingDetails.paymentMonths === displayedCourse?.durationMonths
                                            ? `За ${formatPaymentPeriod(pricingDetails.paymentMonths)}: ${pricingDetails.total.toLocaleString('ru-RU')} ₽ при помесячной оплате.`
                                        : 'Стоимость указана за месяц. Подробности оплаты обсудим при записи.'}
                                </p>
                                {priceWasUpdated && !submittedSnapshot ? <span className={styles.priceUpdated} role="status">Цена обновилась.</span> : null}
                            </> : <p>Стоимость указана за месяц. Подробности оплаты обсудим при записи.</p>}
                        </div>
                        {!priceConfirmed ? (
                            <div className={styles.priceStatus}>
                                {catalogStatus === 'error' ? (
                                    <div className={styles.catalogError} role="alert">
                                        <span>{catalogError}</span>
                                        <button type="button" onClick={retryCatalog}>Повторить</button>
                                    </div>
                                ) : <p className={styles.priceLoading}>{refreshing ? 'Обновляем цену…' : 'Загружаем актуальную цену…'}</p>}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {sent ? (
                    <div className={styles.successState} role="status">
                        <h2 id="admission-title">Заявка отправлена.</h2>
                        <p>Свяжемся с вами, чтобы обсудить обучение.</p>
                        {submittedSnapshot ? <p className={styles.successSummary}>Отправлено: {submittedSnapshot.course.title}, {FORMAT_LABELS[submittedSnapshot.pricing.format]}, {TYPE_LABELS[submittedSnapshot.pricing.type]} — {submittedSnapshot.pricing.price.toLocaleString('ru-RU')} ₽{submittedSnapshot.course.billingUnit === 'course' ? ' за курс' : ' в месяц'}.</p> : null}
                        <button ref={successCloseRef} type="button" className={styles.closeButton} onClick={() => popoutModel.events.setPopout(null)}>Закрыть</button>
                    </div>
                ) : (
                    <form className={styles.form} onSubmit={(event) => { event.preventDefault(); void onSubmit() }} noValidate>
                        <div className={styles.field}>
                            <label htmlFor="admission-name">Имя</label>
                            <input
                                id="admission-name"
                                name="name"
                                type="text"
                                autoComplete="given-name"
                                value={name}
                                disabled={pending}
                                onChange={(event) => setName(event.currentTarget.value)}
                                onBlur={() => setTouched((current) => ({ ...current, name: true }))}
                                aria-invalid={Boolean(nameError)}
                                aria-describedby={nameError ? 'admission-name-error' : undefined}
                            />
                            <p id="admission-name-error" className={`${styles.messageSlot} ${styles.fieldError}`} data-reserve={getNameError('x'.repeat(81), true) ?? ''} aria-hidden={!nameError}><span>{nameError}</span></p>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="admission-phone">Телефон</label>
                            <input
                                id="admission-phone"
                                name="phone"
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                value={phone}
                                disabled={pending}
                                onChange={(event) => setPhone(event.currentTarget.value)}
                                onBlur={() => setTouched((current) => ({ ...current, phone: true }))}
                                aria-invalid={Boolean(phoneError)}
                                aria-describedby={phoneError ? 'admission-phone-error' : undefined}
                            />
                            <p id="admission-phone-error" className={`${styles.messageSlot} ${styles.fieldError}`} data-reserve={getPhoneError('', true) ?? ''} aria-hidden={!phoneError}><span>{phoneError}</span></p>
                        </div>
                        <div className={styles.consent}>
                            <LegalFormDisclaimer variant="light" consentChecked={consentAccepted} onConsentChange={setConsentAccepted} disabled={pending} />
                            <p className={`${styles.messageSlot} ${styles.fieldError}`} data-reserve="Подтвердите согласие." aria-hidden={!consentError} role={consentError ? 'alert' : undefined}><span>{consentError}</span></p>
                        </div>
                        <button type="submit" className={styles.submitButton} disabled={pending || catalogStatus !== 'ready' || refreshing}>
                            {pending ? 'Отправляем…' : 'Отправить заявку'}
                        </button>
                        <p className={`${styles.messageSlot} ${styles.submitError}`} data-reserve="Не получили подтверждение заявки. Попробуйте ещё раз." aria-hidden={!submitError} role={submitError ? 'alert' : undefined}><span>{submitError ? 'Не получили подтверждение заявки. Попробуйте ещё раз.' : null}</span></p>
                    </form>
                )}
            </div>
        </section>
    )
}
